import React from 'react';

import { Layout } from '../Layout/Layout';
import { LayoutItem } from '../Layout/LayoutItem';
import { TilesItem } from '../Tiles/TilesItem';

import type { FileIntakeAccept } from './file-intake.types';
import { FileIntakeUtil } from './file-intake-util';
import { useFileIntakeDrag } from './useFileIntakeDrag';

export type FileIntakeProps = {
  /**
   * What to take, in the `accept` attribute's syntax - `.pdf`, `image/*`, `text/plain`. Omitted,
   * everything is taken: a drop cannot be narrowed by the browser the way a picker's dialog can, so
   * this list is the only filter there is.
   */
  accept?: FileIntakeAccept[];
  children?: React.ReactNode;
  /**
   * Refuse files entirely: no frame while a drag is overhead, nothing taken if one lands, and a
   * paste left to whatever is underneath. For a box that is still on screen but cannot accept
   * anything right now.
   */
  disabled?: boolean;
  /**
   * Shown on the card while a drag is in flight. Without it the frame appears alone, which is the
   * right answer for a target with nothing to say.
   */
  dropMessage?: React.ReactNode;
  name?: string;
  /**
   * Called on every drop, with the files split by `accept`. `rejected` carries names rather than
   * files, because naming them is all a caller can do about them.
   */
  onFiles: (files: { accepted: File[]; rejected: string[] }) => void;
};

/**
 * A box that takes files dropped onto it.
 *
 * **It wraps content rather than standing beside it.** The whole box is the target, so what the
 * reader is looking at is what they can drop on - and it fills whatever it is given, so "the whole
 * box" means the whole of the space its parent has.
 *
 * The frame is drawn over that content for as long as a drag is in flight, and takes no clicks
 * while it is there.
 */
export const FileIntake = ({
  accept,
  children,
  disabled,
  dropMessage,
  name,
  onFiles,
}: FileIntakeProps) => {
  const {
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    isDragging,
  } = useFileIntakeDrag({ accept, disabled, onFiles });

  /*
    **Kept here rather than in the drag hook**, which owns a state machine - what is overhead, how
    deep the pointer is. A paste has none of that: it is one event, answered once.

    Left alone unless it is carrying files and no text, so a pasted paragraph still reaches whatever
    is underneath and is inserted. `preventDefault` only once the files are taken, for the same
    reason - otherwise a text paste would be swallowed by a box that did nothing with it.
  */
  const handlePaste = (event: React.ClipboardEvent<HTMLElement>) => {
    if (disabled) return;
    if (!FileIntakeUtil.isFilePaste({ clipboard: event.clipboardData })) return;

    event.preventDefault();
    onFiles(
      FileIntakeUtil.partition({ accept, files: event.clipboardData.files })
    );
  };

  return (
    /*
      A plain element rather than a `Layout`, because the four handlers have nowhere else to go -
      and all this box owes the frame is a containing block, which is the stylesheet's business.
    */
    <div
      className="file-intake"
      data-test={name}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      {children}
      {/*
        **Mounted only while a drag is in flight**, rather than hidden: a frame that is always there
        is always over the content, and `pointer-events` is one mistake away from swallowing every
        click in the box.

        `middle` on a vertical layout is `align-items`, so it centres the card across the box. Where
        it sits *down* the box is the stylesheet's - the frame is as tall as the content it covers,
        so the card is stuck to the window rather than centred in a page-long box.
      */}
      {isDragging ? (
        <Layout
          className="file-intake-frame"
          direction="vertical"
          name="FILE_INTAKE_FRAME"
          position="middle"
        >
          {dropMessage ? (
            <LayoutItem className="file-intake-message" position="center">
              <TilesItem name="FILE_INTAKE_MESSAGE">{dropMessage}</TilesItem>
            </LayoutItem>
          ) : null}
        </Layout>
      ) : null}
    </div>
  );
};
